/** @module components/hot-spot-quiz */


import Model from '@lernetz/ts-lib/src/core/model/Model';
import {View, ViewRegistrationMap} from '@lernetz/ts-lib/src/core/view/View';
import Repeat from '@lernetz/ts-lib/src/components/Repeat';
import Show from '@lernetz/ts-lib/src/components/Show';

import {HotSpotArea} from '.';
import {Point} from './Point';
import {RectangularHotSpotArea, RectangularHotSpotAreaView} from './RectangularHotSpotArea';
import {Spot, SpotView} from './Spot';

import {Quiz, QuizInputState, QuizVisitor} from '../quiz';

import {HotSpotQuiz as hotSpotQuizTemplate} from '../../templates/hot-spot-quiz';


/**
 * Signifies that this value will be resolved in templates using `div view="[[...]]"` syntax.
 */
type Resolvable = object;


/**
 * [[QuizVisitor]]s that support [[HotSpotQuiz]] should implement this interface.
 */
export interface HotSpotQuizVisitor<TResult = void> extends QuizVisitor<TResult> {
    visitHotSpotQuiz(quiz: HotSpotQuiz): TResult;
}


function isHotSpotQuizVisitor<TResult>(visitor: QuizVisitor<TResult>): visitor is HotSpotQuizVisitor<TResult> {
    return typeof visitor['visitHotSpotQuiz'] !== 'undefined';
}


/**
 * Specifies the initial state of a [[HotSpotQuiz]].
 */
export interface HotSpotQuizInitialState {

    /**
     * Sets the component that should be rendered as the background for this quiz.
     */
    background?: Resolvable;

    /**
     * Sets the defined hot-spot areas for the quiz.
     */
    hotSpotAreas: HotSpotArea[];

    /**
     * Sets the initially placed spots for the quiz.
     */
    spots: Spot[];
}


/**
 * A type of [[Quiz]] that models a hot-spot quiz.
 *
 * @see [[Quiz]], the main interface implemented by this class.
 */
export class HotSpotQuiz extends Model implements Quiz {

    /**
     * Initializes a new instance of the [[HotSpotQuiz]] class.
     *
     * @param initialState Specifies the initial state for the new instance.
     * @param data An optional object to be passed through to the base class' ([[Model]]) constructor.
     */
    public constructor(initialState: HotSpotQuizInitialState, data: object = {}) {
        super({
            background: null,
            hotSpotAreas: [],
            inputState: 'none',
            spots: [],
            ...initialState,
            ...data
        });

        this.updateInputState();
        this.change.filter(({ name }) => name === 'spots').subscribe(() => {
            this.updateHotSpotAreasAndSpots();
            this.updateInputState();
        });
    }

    /**
     * Gets the background of the quiz.
     */
    public readonly background: Resolvable;

    /**
     * Gets the defined hot-spot areas for the quiz.
     */
    public readonly hotSpotAreas: HotSpotArea[];

    /** @inheritdoc */
    public readonly inputState: QuizInputState;

    /**
     * Gets or sets all currently placed spots in the quiz.
     */
    public spots: Spot[];

    /**
     * Dispatches to [[HotSpotQuizVisitor.visitHotSpotQuiz]] (if implemented by `visitor`).
     */
    public accept<TResult = void>(visitor: QuizVisitor<TResult>): TResult {
        if (isHotSpotQuizVisitor(visitor)) {
            return visitor.visitHotSpotQuiz(this);
        }
    }

    /**
     * Callback method that should be invoked when the user clicks or taps
     * at the specified coordinates.
     *
     * This method can be overridden to change the behavior of the quiz.
     * The default implementation adds a new spot, or, if there are already
     * as many spots as there are hot-spot areas, replaces the least recently
     * added spot.
     *
     * @param spot The [[Spot]] where the user clicked or tapped.
     */
    public onSpotClicked(spot: Spot): void {
        if (this.spots.length == this.hotSpotAreas.length) {
            this.spots.shift();
        }
        this.spots = [...this.spots, spot];
    }

    /**
     * Updates the state of all [[hotSpotAreas]] and [[spots]] so that they are
     * consistent with one another.
     */
    protected updateHotSpotAreasAndSpots(): void {
        for (const hotSpotArea of this.hotSpotAreas) {
            hotSpotArea.updateContainedSpots(this.spots);
            for (const containedSpot of hotSpotArea.containedSpots) {
                containedSpot.containedInHotSpotArea = true;
            }
        }
    }

    /**
     * Updates the [[inputState]] property.
     */
    protected updateInputState(): void {
        let inputState: QuizInputState;
        if (this.spots.length) {
            inputState = this.hotSpotAreas.some(hsa => !hsa.containedSpots.length) ? 'incorrect' : 'correct';
        }
        else {
            inputState = 'none';
        }
        this.set('inputState', inputState);
    }
}


/**
 * Base `View` class to render a [[HotSpotQuiz]].
 *
 * This takes care of setting up the default template that renders selected spots
 * on top of the background, and it converts clicks on the area to calls to the
 * [[HotSpotQuiz.onSpotClicked]] callback method.
 *
 * You typically won't need to replace or sub-class this type. Instead,
 * override the CSS styling for the default template.
 */
export class HotSpotQuizView extends View<HotSpotQuiz> {

    public constructor() {
        super({
            template: hotSpotQuizTemplate,
            registrations: HotSpotQuizView.getRegistrations(),
        });
    }

    private static getRegistrations(): ViewRegistrationMap {
        const registrations = new ViewRegistrationMap();
        registrations.set('repeat', Repeat);
        registrations.set('show', Show);
        registrations.set('Spot', SpotView);
        registrations.set(RectangularHotSpotArea, RectangularHotSpotAreaView);
        return registrations;
    }

    protected init(): void {
        this.node.addEventListener('click', this.onClicked.bind(this));
    }

    private onClicked(event: MouseEvent): void {

        const currentTarget = event.currentTarget as HTMLElement;

        // define relative x-coordinates according to position in width of the node
        const x = (event.pageX - currentTarget.offsetLeft) / currentTarget.offsetWidth;
        const y = (event.pageY - currentTarget.offsetTop) / currentTarget.offsetHeight;

        this.data.onSpotClicked(new Spot({ x, y }));
    }
}


/**
 * A set of view registrations for [[HotSpotQuiz]].
 */
export const registrations = new ViewRegistrationMap();
registrations.set(HotSpotQuiz, HotSpotQuizView);
